import {
    CadNextNotification,
    CadNextNotificationEntityEnumType,
    CadNextNotificationTypeEnumType,
} from '@mark43/cad-api';
import { computed } from 'mobx';
import { useCallback, useContext } from 'react';
import { useEventNotificationsEntityId } from './event-notifications-entity-id-context';
import { useEventNotificationsEventId } from './event-notifications-event-id-context';
import { EventNotificationsContext } from './event-notifications-provider';
import { EventNotificationsStore } from './event-notifications-store';

const STABLE_EMPTY_ARRAY: CadNextNotification[] = [];

export function useEventNotification(props: {
    eventId?: number;
    entityId?: number;
    notification?: CadNextNotificationTypeEnumType;
}): CadNextNotification | undefined {
    const eventNotificationsStore = useEventNotificationsStore();
    const eventIdFromContext = useEventNotificationsEventId();
    const entityIdFromContext = useEventNotificationsEntityId();

    if (!eventNotificationsStore) {
        throw new Error('Unexpectedly did not find an eventNotificationsStore on Context');
    }

    const notification = props.notification;

    if (!notification) {
        return undefined;
    }

    const eventIdToUse = props.eventId ?? eventIdFromContext;
    const entityIdToUse = props.entityId ?? entityIdFromContext;

    if (!eventIdToUse || !entityIdToUse) {
        return;
    }

    // Wrapping this in `computed` prevents consumers from re-rendering if the same primitive value is returned from evaluation
    // as the last time this was evaluated
    return computed(() =>
        eventNotificationsStore.getNotification({
            eventId: eventIdToUse,
            entityId: entityIdToUse,
            notification,
        })
    ).get();
}

export function useEventNotifications(props: {
    eventId?: number;
    entityId?: number;
    notifications?: CadNextNotificationTypeEnumType[];
}): CadNextNotification[] {
    const eventNotificationsStore = useEventNotificationsStore();
    const eventIdFromContext = useEventNotificationsEventId();
    const entityIdFromContext = useEventNotificationsEntityId();

    if (!eventNotificationsStore) {
        throw new Error('Unexpectedly did not find an eventNotificationsStore on Context');
    }

    const notifications = props.notifications;

    if (!notifications) {
        return STABLE_EMPTY_ARRAY;
    }

    const eventIdToUse = props.eventId ?? eventIdFromContext;
    const entityIdToUse = props.entityId ?? entityIdFromContext;

    if (!eventIdToUse || !entityIdToUse) {
        return STABLE_EMPTY_ARRAY;
    }

    // Wrapping this in `computed` prevents consumers from re-rendering if the same primitive value is returned from evaluation
    // as the last time this was evaluated
    return computed(() => {
        const ret = eventNotificationsStore.getNotifications({
            eventId: eventIdToUse,
            entityId: entityIdToUse,
            notifications,
        });
        if (notifications.length > 0) return ret;
        return STABLE_EMPTY_ARRAY;
    }).get();
}

export function useAllEventNotifications(eventId: number): CadNextNotification[] {
    const eventNotificationsStore = useEventNotificationsStore();
    const eventIdFromContext = useEventNotificationsEventId();
    const eventIdToUse = eventId ?? eventIdFromContext;
    if (!eventNotificationsStore) {
        throw new Error('Unexpectedly did not find an eventNotificationsStore on Context');
    }
    return eventNotificationsStore.getAllNotificationsForEvent(eventIdToUse);
}

export function useAllEventNotificationsForEntityTypeForEvent(args: {
    eventId?: number;
    entityType?: CadNextNotificationEntityEnumType;
}): CadNextNotification[] {
    const eventNotificationsStore = useEventNotificationsStore();
    const eventIdFromContext = useEventNotificationsEventId();
    const eventIdToUse = args.eventId ?? eventIdFromContext;
    if (!eventIdToUse || !args.entityType) {
        return [];
    }
    if (!eventNotificationsStore) {
        throw new Error('Unexpectedly did not find an eventNotificationsStore on Context');
    }
    return eventNotificationsStore.getAllNotificationsForEntityTypeForEvent(
        eventIdToUse,
        args.entityType
    );
}

export function useRemoveEventNotifications() {
    const eventNotificationsStore = useEventNotificationsStore();
    const eventIdFromContext = useEventNotificationsEventId();
    const entityIdFromContext = useEventNotificationsEntityId();
    if (!eventNotificationsStore) {
        throw new Error('Unexpectedly did not find an eventNotificationsStore on Context');
    }
    return useCallback(
        (props: {
            eventId?: number;
            entityId?: number;
            notifications?: CadNextNotificationTypeEnumType[];
        }) => {
            const eventIdToUse = props.eventId ?? eventIdFromContext;
            const entityIdToUse = props.entityId ?? entityIdFromContext;
            if (!eventIdToUse || !entityIdToUse || !props.notifications) {
                return;
            }
            eventNotificationsStore.removeNotifications({
                eventId: eventIdToUse,
                entityId: entityIdToUse,
                notifications: props.notifications,
            });
        },
        [entityIdFromContext, eventIdFromContext, eventNotificationsStore]
    );
}

export function useEventNotificationsStore(): EventNotificationsStore {
    const eventNotificationsStore = useContext(EventNotificationsContext);
    if (!eventNotificationsStore) {
        throw new Error('Unexpectedly did not find an eventNotificationsStore on Context');
    }
    return eventNotificationsStore;
}
